import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';
import PremiumCard from './ui/PremiumCard';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

const iconMap: Record<string, React.ReactNode> = {
  problem: <LightbulbOutlinedIcon sx={{ fontSize: 22 }} />,
  learning: <MenuBookOutlinedIcon sx={{ fontSize: 22 }} />,
  ownership: <RocketLaunchOutlinedIcon sx={{ fontSize: 22 }} />,
  quality: <VerifiedOutlinedIcon sx={{ fontSize: 22 }} />,
};

function EngineeringPhilosophy() {
  const journey = setup.technologyJourney;

  return (
    <MotionSection id="philosophy" muted>
      <SectionHeader
        eyebrow="Principles"
        title="Engineering Philosophy"
        subtitle="How I think about problems, technology, and continuous growth."
      />

      {journey && (
        <blockquote className="ui-card ui-card-lift p-6 sm:p-8 mb-10 border-l-4 border-l-accent">
          <p className="text-lg sm:text-xl text-ink leading-relaxed font-medium tracking-[-0.01em]">
            &ldquo;{journey.quote}&rdquo;
          </p>
        </blockquote>
      )}

      {journey?.steps && (
        <div className="mb-10">
          <p className="ui-eyebrow mb-5">Technology Journey</p>
          {/* Mobile: vertical */}
          <div className="flex flex-col items-center max-w-xs mx-auto md:hidden">
            {journey.steps.map((step, index) => (
              <React.Fragment key={step.label}>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  className="w-full ui-card ui-card-lift p-4 text-center"
                >
                  <p className="text-base font-semibold text-ink">{step.label}</p>
                  {step.note && (
                    <p className="text-xs text-ink-muted mt-1 leading-relaxed">{step.note}</p>
                  )}
                </motion.div>
                {index < journey.steps.length - 1 && (
                  <span className="text-ink-faint py-1.5 text-sm" aria-hidden>↓</span>
                )}
              </React.Fragment>
            ))}
          </div>
          {/* Desktop: horizontal scroll */}
          <div className="hidden md:block overflow-x-auto pb-2">
            <div className="flex items-center gap-2 min-w-max px-1">
              {journey.steps.map((step, index) => (
                <React.Fragment key={step.label}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: index * 0.04 }}
                    className="ui-card ui-card-lift p-4 text-center min-w-[120px] max-w-[140px]"
                  >
                    <p className="text-sm font-semibold text-ink">{step.label}</p>
                    {step.note && (
                      <p className="text-[11px] text-ink-muted mt-1 leading-snug">{step.note}</p>
                    )}
                  </motion.div>
                  {index < journey.steps.length - 1 && (
                    <span className="text-ink-faint text-sm flex-shrink-0" aria-hidden>→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {setup.engineeringPhilosophy.map((item, index) => (
          <PremiumCard
            key={item.title}
            icon={iconMap[item.icon]}
            title={item.title}
            description={item.description}
            index={index}
          />
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/timeline"
          className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
        >
          View full engineering timeline
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Link>
      </div>
    </MotionSection>
  );
}

export default EngineeringPhilosophy;
